import React, { useEffect, useState } from 'react';
import lmsApi from '../../services/api';
import { formatDate, formatTime } from '../../utils/dateFormatter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Search, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import StaffLayout from '../../components/StaffLayout';

interface Transaction {
    id: string;
    loan_application_number: string;
    transaction_type: string;
    amount: string | number;
    status: string;
    description: string;
    transaction_date: string;
    reference_id: string;
    bill_type?: string;
}

const StaffTransactionsPage = () => {
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('PENDING');

    const fetchTransactions = async () => {
        setLoading(true);
        try {
            const params: any = {};
            if (filterStatus) params.status = filterStatus;
            const response = await lmsApi.post('/staff/transactions/list_post/', params);
            setTransactions(response.data);
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch transactions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, [filterStatus]);

    const handleAction = async (id: string, action: 'success' | 'failed') => {
        try {
            const endpoint = `/staff/transactions/${id}/mark_${action}/`;
            await lmsApi.post(endpoint);
            toast.success(`Transaction marked as ${action}`);
            fetchTransactions();
        } catch (error) {
            console.error(error);
            toast.error(`Failed to mark transaction as ${action}`);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'SUCCESS': return <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">Success</Badge>;
            case 'PENDING': return <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">Pending</Badge>;
            case 'FAILED': return <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">Failed</Badge>;
            default: return <Badge variant="outline">{status}</Badge>;
        }
    };

    const filteredTransactions = transactions.filter(t =>
        t.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.reference_id?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <StaffLayout title="Transaction Management" userName="Staff Administrator">
            <Card className="shadow-sm border-0 ring-1 ring-gray-200 overflow-hidden">
                <CardHeader className="border-b bg-white p-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <CardTitle className="text-xl font-bold">Manage Transactions</CardTitle>
                            <p className="text-sm text-gray-500 mt-1">Review and approve pending charges or payments.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant={filterStatus === 'PENDING' ? 'default' : 'secondary'}
                                size="sm"
                                onClick={() => setFilterStatus('PENDING')}
                                className="rounded-full"
                            >
                                Pending
                            </Button>
                            <Button
                                variant={filterStatus === 'SUCCESS' ? 'default' : 'secondary'}
                                size="sm"
                                onClick={() => setFilterStatus('SUCCESS')}
                                className="rounded-full"
                            >
                                Success
                            </Button>
                            <Button
                                variant={filterStatus === 'FAILED' ? 'default' : 'secondary'}
                                size="sm"
                                onClick={() => setFilterStatus('FAILED')}
                                className="rounded-full"
                            >
                                Failed
                            </Button>
                            <Button
                                variant={filterStatus === '' ? 'default' : 'secondary'}
                                size="sm"
                                onClick={() => setFilterStatus('')}
                                className="rounded-full"
                            >
                                All
                            </Button>
                        </div>
                    </div>
                    <div className="mt-6 relative max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Search by ID or Description..."
                            className="pl-10 h-10 ring-offset-background"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center p-20 gap-4">
                            <Loader2 className="h-10 w-10 animate-spin text-primary opacity-50" />
                            <p className="text-gray-500 font-medium italic">Fetching transactions...</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50/50">
                                    <TableHead className="w-[180px]">Date</TableHead>
                                    <TableHead>Loan App No</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Bill Type</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredTransactions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-24 text-gray-400">
                                            <div className="flex flex-col items-center gap-2">
                                                <Search className="h-10 w-10 opacity-10" />
                                                <p>No transactions found matching the criteria.</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredTransactions.map((txn) => (
                                        <TableRow key={txn.id} className="group hover:bg-blue-50/30 transition-colors">
                                            <TableCell className="font-medium whitespace-nowrap">
                                                <div className="text-sm font-semibold text-gray-900">{formatDate(txn.transaction_date)}</div>
                                                <div className="text-[11px] text-gray-400 font-mono mt-0.5">{formatTime(txn.transaction_date)}</div>
                                            </TableCell>
                                            <TableCell className="font-medium text-blue-600">
                                                {txn.loan_application_number}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm ${txn.transaction_type === 'CREDIT' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                    }`}>
                                                    {txn.transaction_type}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {txn.bill_type ? (
                                                    <span className="text-xs font-medium bg-gray-100 px-2 py-1 rounded text-gray-700">
                                                        {txn.bill_type}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400">-</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="font-bold text-gray-900">
                                                ₹{parseFloat(txn.amount as string).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </TableCell>
                                            <TableCell>
                                                {getStatusBadge(txn.status)}
                                            </TableCell>
                                            <TableCell className="max-w-xs truncate text-gray-600 text-sm italic">
                                                {txn.description || <span className="text-gray-300">No description</span>}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {txn.status === 'PENDING' && (
                                                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all duration-200">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-8 bg-white text-green-600 border-green-200 hover:bg-green-600 hover:text-white hover:border-green-600 transition-all"
                                                            onClick={() => handleAction(txn.id, 'success')}
                                                        >
                                                            <CheckCircle2 className="mr-1.5 h-4 w-4" />
                                                            Success
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-8 bg-white text-red-600 border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 transition-all"
                                                            onClick={() => handleAction(txn.id, 'failed')}
                                                        >
                                                            <XCircle className="mr-1.5 h-4 w-4" />
                                                            Fail
                                                        </Button>
                                                    </div>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </StaffLayout>
    );
};

export default StaffTransactionsPage;
